import { ArrowLeft, Plus } from "lucide-react";
import { GenieAvatar } from "@/components/GenieAvatar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Agent } from "@/types/agent/agent";
import { GenieCategories } from "@/types/shared/categories";
import { DEFAULT_BRANDING } from "@/types/ui/branding";

export interface GenieKnowledgeHeaderProps {
  genie: Agent;
  documentCount: number;
  lastUpdated: Date | null;
  onBack: () => void;
  onAddKnowledge: () => void;
  /**
   * Replaces the default "N sources" line. The documents page counts documents
   * it made, not sources it read, and saying "sources" there would be a lie.
   */
  metaLabel?: string;
  /** Replaces the default "Add knowledge" button on sibling pages. */
  primaryAction?: { label: string; onClick: () => void };
}

/**
 * Slim identity bar for a single Genie's knowledge page (~72px tall).
 * Replaces the old 424px hero card — back link, small avatar, name,
 * category/status, a plain source count, and the primary "add knowledge"
 * action, all in one row. The welcome message is content, not chrome, so it
 * does not live here; it stays on the Genie's own page/detail views.
 */
export const GenieKnowledgeHeader = ({
  genie,
  documentCount,
  lastUpdated,
  onBack,
  onAddKnowledge,
  metaLabel,
  primaryAction,
}: GenieKnowledgeHeaderProps) => {
  // Same branding/category resolution as the hero card it replaces (and the
  // rest of MyGenies), so a branded Genie still reads as branded here.
  const brand = genie.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
  const hasBranding: boolean = Boolean(
    genie.branding &&
      (genie.branding.primaryColor !== DEFAULT_BRANDING.primaryColor ||
        genie.branding.secondaryColor ||
        genie.branding.customAvatarUrl ||
        genie.branding.gradientEnabled),
  );

  // `category_data.label` is a bare string, so it cannot carry a colour. The page
  // this replaced read `.color` off it regardless and produced "undefined55"
  // borders for any Genie with custom category data — resolve the swatch off the
  // known category list and only take the label from `category_data`.
  const categoryEntry =
    GenieCategories.find((cat) => cat.id === genie.category) ??
    GenieCategories.find((cat) => cat.id === "other")!;
  const category = {
    label: genie.category_data?.label ?? categoryEntry.label,
    color: categoryEntry.color,
  };

  const sourceLabel = documentCount === 1 ? "source" : "sources";
  const updatedLabel = lastUpdated ? ` · updated ${lastUpdated.toLocaleDateString()}` : "";

  return (
    <div className="relative mb-6 flex flex-wrap items-center gap-4 rounded-xl border border-gray-200 bg-white py-3 pl-5 pr-4 md:pr-5">
      {/* Restrained brand tell: a hairline accent, not a full gradient block. */}
      {hasBranding && (
        <span
          className="absolute inset-y-0 left-0 w-[3px] rounded-l-xl"
          style={{ backgroundColor: brand }}
          aria-hidden
        />
      )}

      <button
        type="button"
        onClick={onBack}
        className="flex flex-shrink-0 items-center gap-2 text-gray-600 transition-colors hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="text-sm font-medium">All Genies</span>
      </button>

      <div className="hidden h-8 w-px flex-shrink-0 bg-gray-200 sm:block" aria-hidden />

      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
        <div className="flex-shrink-0">
          <GenieAvatar
            color={brand}
            secondaryColor={genie.branding?.secondaryColor}
            gradientEnabled={genie.branding?.gradientEnabled}
            gradientAngle={genie.branding?.gradientAngle}
            avatarBackgroundOpacity={genie.branding?.avatarBackgroundOpacity}
            speaking={false}
            listening={false}
            isConnecting={false}
            customAvatarUrl={genie.branding?.logoUrl || genie.branding?.customAvatarUrl}
            hasCustomBranding={hasBranding}
            size={40}
          />
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h1 className="truncate text-xl font-semibold text-gray-900 md:text-2xl">
            {genie.name}
          </h1>
          <span
            className="rounded-full border bg-white px-2 py-0.5 text-[10px] font-medium shadow-sm"
            style={{ color: category.color, borderColor: `${category.color}55` }}
          >
            {category.label}
          </span>
          <span
            className="inline-flex items-center rounded-full border bg-white px-2 py-0.5 text-[10px] shadow-sm"
            style={{ borderColor: `${brand}55` }}
          >
            <span
              className={cn(
                "mr-1.5 h-1.5 w-1.5 rounded-full",
                genie.is_active ? "bg-green-500" : "bg-gray-400",
              )}
              aria-hidden
            />
            <span
              className={cn("font-medium", genie.is_active ? "" : "text-gray-600")}
              style={genie.is_active ? { color: brand } : undefined}
            >
              {genie.is_active ? "Active" : "Inactive"}
            </span>
          </span>
        </div>
      </div>

      <div className="flex flex-shrink-0 flex-wrap items-center gap-3">
        <span className="text-xs text-gray-500 md:text-sm">
          {metaLabel ?? `${documentCount} ${sourceLabel}${updatedLabel}`}
        </span>
        <Button
          onClick={primaryAction?.onClick ?? onAddKnowledge}
          className="gap-2 bg-gradient-to-r from-genie-primary to-genie-secondary font-semibold text-white shadow-md shadow-genie-primary/20 hover:from-genie-primary-hover hover:to-genie-secondary"
        >
          <Plus className="h-4 w-4" />
          {primaryAction?.label ?? "Add knowledge"}
        </Button>
      </div>
    </div>
  );
};

export default GenieKnowledgeHeader;
